// src/pages/planner.jsx
import { useEffect, useState, Fragment } from 'react'
import useMealPlanner from '../hooks/useMealPlanner'
import Button from '../components/Shared/Button'
import Spinner from '../components/Shared/Spinner'
import Input from '../components/Shared/Input'
import Tag from '../components/Shared/Tag'
import { format, differenceInCalendarDays, parseISO } from 'date-fns'
import { useNavigate } from 'react-router-dom'

export default function PlannerPage() {
  const navigate = useNavigate()
  const {
    plans,
    fetchAll,
    setActivePlan,
    deletePlan,
    duplicatePlan,
  } = useMealPlanner()

  const [loading, setLoading] = useState(true)
  const [pending, setPending] = useState({ id: null, action: null })
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [compareMode, setCompareMode] = useState(false)
  const [selectedPlans, setSelectedPlans] = useState([])

  useEffect(() => {
    fetchAll().finally(() => setLoading(false))
  }, [fetchAll])

  const handleDelete = async (id) => {
    if (pending.id) return
    setPending({ id, action: 'delete' })
    try {
      await deletePlan(id)
      await fetchAll()
    } catch (err) {
      console.error('Failed to delete plan', err)
    } finally {
      setPending({ id: null, action: null })
    }
  }

  const handleDuplicate = async (id) => {
    if (pending.id) return
    setPending({ id, action: 'duplicate' })
    try {
      await duplicatePlan(id)
      await fetchAll()
    } catch (err) {
      console.error('Failed to duplicate plan', err)
    } finally {
      setPending({ id: null, action: null })
    }
  }

  const handleOpen = (plan) => {
    setActivePlan(plan)
    navigate(`/planner/${plan.id}`)
  }

  const filterPlans = () => {
    let result = plans

    if (search.trim()) {
      const lower = search.toLowerCase()
      result = result.filter(
        p =>
          p.title?.toLowerCase().includes(lower) ||
          p.client_tag?.toLowerCase().includes(lower)
      )
    }

    const today = new Date()
    if (filterStatus === 'upcoming') {
      result = result.filter(p => new Date(p.start_date) >= today)
    } else if (filterStatus === 'past') {
      result = result.filter(p => new Date(p.end_date) < today)
    }

    return result
  }

  const filteredPlans = filterPlans()

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-semibold text-charcoal">📅 Meal Plans</h1>

      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <Input
          placeholder="Search by title or client..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div className="flex gap-2 text-sm">
          {['all', 'upcoming', 'past'].map(status => (
            <Button
              key={status}
              size="sm"
              intent={filterStatus === status ? 'primary' : 'secondary'}
              onClick={() => setFilterStatus(status)}
            >
              {status === 'all' ? 'All Plans' : status.charAt(0).toUpperCase() + status.slice(1)}
            </Button>
          ))}
          <Button
            size="sm"
            intent={compareMode ? 'primary' : 'secondary'}
            onClick={() => setCompareMode(!compareMode)}
          >
            {compareMode ? 'Exit Compare Mode' : 'Compare Plans'}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Spinner size="lg" />
        </div>
      ) : filteredPlans.length === 0 ? (
        <div className="text-center text-muted italic">No plans match your filters.</div>
      ) : (
        <Fragment>
          {compareMode && selectedPlans.length > 0 && (
            <div className="p-4 bg-gray-50 rounded border text-sm">
              <h3 className="font-semibold mb-2">🆚 Comparing {selectedPlans.length} Plan(s)</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {selectedPlans.map(plan => (
                  <div key={plan.id} className="bg-white border p-3 rounded shadow-sm">
                    <h4 className="font-medium text-charcoal">{plan.title}</h4>
                    <p className="text-muted text-xs">{plan.client_tag}</p>
                    <p className="text-xs text-muted">
                      {plan.start_date && plan.end_date ? (
                        <>
                          {format(new Date(plan.start_date), 'PPP')} → {format(new Date(plan.end_date), 'PPP')}
                        </>
                      ) : (
                        <span className="italic text-red-400">Missing dates</span>
                      )}
                    </p>
                    {plan.start_date && plan.end_date && (
                      <Tag className="mt-1 inline-block text-xs">
                        {differenceInCalendarDays(parseISO(plan.end_date), parseISO(plan.start_date)) + 1}-day plan
                      </Tag>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <ul className="grid gap-4">
            {filteredPlans.map((plan) => {
              const days =
                plan.start_date && plan.end_date
                  ? differenceInCalendarDays(parseISO(plan.end_date), parseISO(plan.start_date)) + 1
                  : 0

              return (
                <li
                  key={plan.id}
                  className="p-4 border rounded shadow-sm bg-white hover:shadow-md transition"
                >
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center">
                      {compareMode && (
                        <input
                          type="checkbox"
                          checked={selectedPlans.some(p => p.id === plan.id)}
                          onChange={() => {
                            if (selectedPlans.some(p => p.id === plan.id)) {
                              setSelectedPlans(prev => prev.filter(p => p.id !== plan.id))
                            } else {
                              setSelectedPlans(prev => [...prev, plan])
                            }
                          }}
                          className="mr-2"
                        />
                      )}
                      <div>
                        <h2 className="text-lg font-medium text-charcoal">{plan.title}</h2>
                        <p className="text-sm text-muted">{plan.client_tag}</p>
                        <p className="text-xs text-muted">
                          {plan.start_date && plan.end_date ? (
                            <>
                              {format(new Date(plan.start_date), 'PPP')} → {format(new Date(plan.end_date), 'PPP')}
                            </>
                          ) : (
                            <span className="italic text-red-400">Missing dates</span>
                          )}
                        </p>
                        {days > 0 && (
                          <Tag className="mt-1 inline-block">
                            {days}-day plan
                          </Tag>
                        )}
                        {days >= 14 && (
                          <Tag className="ml-2 text-xs bg-soft-yellow">
                            Long-term
                          </Tag>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        intent="primary"
                        onClick={() => handleOpen(plan)}
                      >
                        Open
                      </Button>
                      <Button
                        size="sm"
                        intent="secondary"
                        onClick={() => handleDuplicate(plan.id)}
                        disabled={pending.id === plan.id}
                      >
                        {pending.id === plan.id && pending.action === 'duplicate' ? 'Duplicating…' : 'Duplicate'}
                      </Button>
                      <Button
                        size="sm"
                        intent="danger"
                        onClick={() => handleDelete(plan.id)}
                        disabled={pending.id === plan.id}
                      >
                        {pending.id === plan.id && pending.action === 'delete' ? 'Deleting…' : 'Delete'}
                      </Button>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
        </Fragment>
      )}
    </div>
  )
}